import React, { useRef } from "react";
import { t } from "../i18n";
import { CheckIcon } from "./Icons";

// Выбор темы — плитками с миниатюрой панели.
//
// Цвета в миниатюрах — самих тем, а не текущей: плитка показывает, какой
// панель станет после нажатия. Иначе три индиговые темы и две цветные
// различались бы только подписью. Значения — те же, что в theme/palette.js,
// ThemeProvider и index.css; основной цвет — 500-я ступень акцента.
//
// Всё оформление — классами theme-picker-* в index.css, а цвета темы уходят
// туда переменными. Настройки в светлой теме перекрашивают встроенные цвета
// через !important по атрибуту style, и миниатюры, заданные встроенными
// цветами, перекрасились бы вместе с ними.
const THEMES = [
  {
    id: "dark",
    label: "Тёмная",
    colorName: "Индиго",
    caption: "Для долгой работы",
    accent: "#6c73f4",
    accentSoft: "#8a90fe",
    canvas: "#0a0b0f",
    surface: "#0e1015",
    raised: "#171a22",
    ink: "#e2e4e9",
    faint: "#2a2e39"
  },
  {
    id: "light",
    label: "Светлая",
    colorName: "Индиго",
    caption: "Высокий контраст",
    accent: "#6c73f4",
    accentSoft: "#8a90fe",
    canvas: "#eceef2",
    surface: "#ffffff",
    raised: "#f1f2f6",
    ink: "#1f2430",
    faint: "#dcdfe6"
  },
  {
    // Убрана из выбора (решение владельца панели, 23.10.2026): без неё тем шесть,
    // ровно два ряда по три. Сама тема осталась — у кого она уже выбрана,
    // работает как прежде, пока человек не выберет другую.
    hidden: true,
    id: "graphite",
    label: "Графит",
    colorName: "Индиго",
    caption: "Строгий тёмно-серый",
    accent: "#6c73f4",
    accentSoft: "#8a90fe",
    canvas: "#1c1f23",
    surface: "#262a30",
    raised: "#30353c",
    ink: "#f0f1f4",
    faint: "#3d424a"
  },
  {
    id: "maroon",
    label: "Бордовая",
    colorName: "Тёмно-бордовый",
    caption: "Тёмная с бордовым",
    accent: "#660000",
    accentSoft: "#a32020",
    canvas: "#0a0b0f",
    surface: "#0e1015",
    raised: "#171a22",
    ink: "#e2e4e9",
    faint: "#2a2e39"
  },
  {
    id: "crimson",
    label: "Красная",
    colorName: "Мягкий красный",
    caption: "Тёмная с красным",
    accent: "#d65f5f",
    accentSoft: "#e07c7c",
    canvas: "#0a0b0f",
    surface: "#0e1015",
    raised: "#171a22",
    ink: "#e2e4e9",
    faint: "#2a2e39"
  },
  {
    id: "green",
    label: "Зелёная",
    colorName: "Мягкий зелёный",
    caption: "Тёмная с зелёным",
    accent: "#3a8f69",
    accentSoft: "#5bb38a",
    canvas: "#0a0b0f",
    surface: "#0e1015",
    raised: "#171a22",
    ink: "#e2e4e9",
    faint: "#2a2e39"
  },
  {
    id: "white",
    label: "Белая",
    colorName: "Белый",
    caption: "Тёмная с белым",
    accent: "#eceef2",
    accentSoft: "#d2d5db",
    // Галочка выбранной плитки стоит на цвете акцента: на белом — тёмная.
    onAccent: "#0e1015",
    canvas: "#0a0b0f",
    surface: "#0e1015",
    raised: "#171a22",
    ink: "#e2e4e9",
    faint: "#2a2e39"
  }
];

const hexToRgb = (hex) => [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16)).join(", ");

const themeVars = (theme) => ({
  "--tp-accent": theme.accent,
  "--tp-accent-rgb": hexToRgb(theme.accent),
  "--tp-accent-soft": theme.accentSoft,
  "--tp-on": theme.onAccent || "#ffffff",
  "--tp-canvas": theme.canvas,
  "--tp-surface": theme.surface,
  "--tp-raised": theme.raised,
  "--tp-ink": theme.ink,
  "--tp-faint": theme.faint
});

// Миниатюра панели: боковое меню с активным пунктом, заголовок, две плитки,
// переписка с исходящим сообщением и кнопка — всё, где виден акцент.
const ThemePreview = () => (
  <span className="theme-picker-preview" aria-hidden="true">
    <span className="theme-picker-window">
      <span className="theme-picker-rail">
        <i className="is-active" />
        <i />
        <i />
        <i />
      </span>
      <span className="theme-picker-main">
        <span className="theme-picker-heading" />
        <span className="theme-picker-tiles">
          <i />
          <i />
        </span>
        <span className="theme-picker-chat">
          <i className="is-in" />
          <i className="is-out" />
        </span>
        <span className="theme-picker-button" />
      </span>
    </span>
  </span>
);

// Темы, которые показываются в выборе.
const PICKABLE_THEMES = THEMES.filter((theme) => !theme.hidden);

const ThemePicker = ({ value, onChange, isPhoneView = false }) => {
  const groupRef = useRef(null);

  // Стрелками — как у обычной группы переключателей: фокус и выбор идут
  // вместе, по кругу.
  const handleKeyDown = (event) => {
    const step = event.key === "ArrowRight" || event.key === "ArrowDown"
      ? 1
      : (event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0);
    if (!step) return;
    event.preventDefault();
    const index = Math.max(0, PICKABLE_THEMES.findIndex((theme) => theme.id === value));
    const next = PICKABLE_THEMES[(index + step + PICKABLE_THEMES.length) % PICKABLE_THEMES.length];
    onChange?.(next.id);
    const button = groupRef.current?.querySelector(`[data-theme-id="${next.id}"]`);
    if (button) button.focus();
  };

  return (
    <div
      ref={groupRef}
      className={`theme-picker${isPhoneView ? " is-phone" : ""}`}
      role="radiogroup"
      aria-label={t("Тема оформления")}
      onKeyDown={handleKeyDown}
    >
      {PICKABLE_THEMES.map((theme) => {
        const active = value === theme.id;
        return (
          <button
            key={theme.id}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            data-theme-id={theme.id}
            className={`theme-picker-card${active ? " is-active" : ""}`}
            style={themeVars(theme)}
            onClick={() => onChange?.(theme.id)}
          >
            <ThemePreview />
            {active && (
              <span className="theme-picker-check" aria-hidden="true">
                <CheckIcon size={12} strokeWidth={3} />
              </span>
            )}
            <span className="theme-picker-meta">
              <span className="theme-picker-name">{t(theme.label)}</span>
              <span className="theme-picker-color">
                <span className="theme-picker-swatch" />
                {t(theme.colorName)}
              </span>
              <span className="theme-picker-caption">{t(theme.caption)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default ThemePicker;
